import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingCart, Star, Eye, Plus, Minus, BadgeCheck, Zap, Package, ChevronRight } from "lucide-react";
import { useSiteConfig } from "../../context/SiteConfigContext";
import { useCart } from "../../context/CartContext";
import { showError } from "../../utils/toastUtils";
import Auth from "../../utils/AuthUtils";
import wishlistAPI from "../../api/wishlistApi";

/* Is card ki poori CSS yahin hai (mpc-* prefix) taaki header ke
   .med-icon-btn jaisi classes se conflict na ho. */
const styles = `
.mpc-card {
  background: var(--med-surface, #fff);
  border-radius: var(--med-radius-lg, 12px);
  border: 1px solid var(--med-border, #e2e8f0);
  box-shadow: var(--med-shadow-card, 0 2px 8px rgba(26,35,51,0.08));
  overflow: hidden;
  transition: box-shadow 250ms ease, transform 250ms ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: var(--med-font-family, 'DM Sans', system-ui, sans-serif);
}
.mpc-card:hover {
  box-shadow: var(--med-shadow-md, 0 4px 12px rgba(10,124,165,0.12));
  transform: translateY(-2px);
}

/* image zone */
.mpc-img-wrap {
  position: relative;
  background: var(--med-surface-secondary, #f5f7fa);
  overflow: hidden;
  aspect-ratio: 4 / 3;
}
.mpc-img-link { display: block; width: 100%; height: 100%; }
.mpc-img-wrap img {
  width: 100%; height: 100%;
  object-fit: contain; display: block;
  transition: transform 400ms ease;
  mix-blend-mode: multiply;
}
.mpc-card:hover .mpc-img-wrap img { transform: scale(1.04); }

.mpc-badges {
  position: absolute; top: 12px; left: 12px;
  display: flex; flex-direction: column; gap: 6px; z-index: 2;
}
.mpc-badge {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 600;
  padding: 3px 10px; border-radius: 999px; white-space: nowrap;
}
.mpc-badge-danger  { background: var(--med-danger-bg, #fdecea);  color: var(--med-danger-text, #8b2820); }
.mpc-badge-warning { background: var(--med-warning-bg, #fdf3e7); color: var(--med-warning-text, #9b5b1a); }
.mpc-badge-success { background: var(--med-success-bg, #e8f8f1); color: var(--med-success-text, #145c3e); }

.mpc-actions {
  position: absolute; top: 12px; right: 12px;
  display: flex; flex-direction: column; gap: 6px; z-index: 2;
}
.mpc-icon-btn {
  width: 34px; height: 34px;
  border-radius: var(--med-radius-md, 8px);
  background: var(--med-surface, #fff);
  border: 1px solid var(--med-border, #e2e8f0);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--med-text-secondary, #4a5568);
  transition: all 150ms ease; padding: 0; text-decoration: none;
}
.mpc-icon-btn:hover {
  background: var(--med-primary-light, #e6f5fa);
  color: var(--med-primary, #0a7ca5);
  border-color: var(--med-primary-muted, #b3dcea);
}
.mpc-icon-btn.active {
  background: var(--med-danger-bg, #fdecea);
  color: var(--med-danger, #c0392b);
  border-color: var(--med-danger-bg, #fdecea);
}

/* body */
.mpc-body {
  padding: 16px 18px 12px; flex: 1;
  display: flex; flex-direction: column; gap: 10px;
}
.mpc-meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.mpc-category {
  font-size: 11px; font-weight: 600;
  color: var(--med-primary, #0a7ca5);
  text-transform: uppercase; letter-spacing: 0.05em;
}
.mpc-rating {
  display: flex; align-items: center; gap: 4px;
  font-size: 12px; font-weight: 600;
  color: var(--med-warning, #d9832a);
}
.mpc-rating-count { color: var(--med-text-muted, #8a96a8); font-weight: 400; }

.mpc-title {
  font-size: 16px; font-weight: 700; line-height: 1.3; margin: 0;
  color: var(--med-text-primary, #1a2333);
}
.mpc-title a { color: inherit; text-decoration: none; }
.mpc-title a:hover { color: var(--med-primary, #0a7ca5); }

.mpc-sku {
  font-family: var(--med-font-mono, 'DM Mono', monospace);
  font-size: 11px; color: var(--med-text-muted, #8a96a8);
}
.mpc-desc {
  font-size: 12px; line-height: 1.5; margin: 0;
  color: var(--med-text-secondary, #4a5568);
  display: -webkit-box; -webkit-line-clamp: 2;
  -webkit-box-orient: vertical; overflow: hidden;
}

/* footer */
.mpc-footer {
  padding: 12px 18px 16px;
  border-top: 1px solid var(--med-border, #e2e8f0);
  display: flex; flex-direction: column; gap: 12px;
}
.mpc-price-row { display: flex; align-items: baseline; flex-wrap: wrap; gap: 6px; }
.mpc-price {
  font-size: 22px; font-weight: 700; line-height: 1;
  color: var(--med-text-primary, #1a2333);
}
.mpc-price-del {
  font-size: 12px; text-decoration: line-through;
  color: var(--med-text-muted, #8a96a8);
}
.mpc-save {
  font-size: 11px; font-weight: 600;
  color: var(--med-success-text, #145c3e);
  background: var(--med-success-bg, #e8f8f1);
  padding: 2px 8px; border-radius: 999px;
}

.mpc-qty-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.mpc-qty-label { font-size: 12px; font-weight: 500; color: var(--med-text-muted, #8a96a8); }
.mpc-qty-ctrl {
  display: flex; align-items: center;
  border: 1px solid var(--med-border, #e2e8f0);
  border-radius: var(--med-radius-md, 8px);
  overflow: hidden; height: 34px;
}
.mpc-qty-btn {
  width: 34px; height: 34px; padding: 0;
  background: var(--med-surface-secondary, #f5f7fa);
  border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: var(--med-text-secondary, #4a5568);
  transition: background 150ms ease, color 150ms ease;
}
.mpc-qty-btn:hover { background: var(--med-primary-light, #e6f5fa); color: var(--med-primary, #0a7ca5); }
.mpc-qty-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.mpc-qty-val {
  min-width: 36px; height: 100%;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 600;
  font-family: var(--med-font-mono, 'DM Mono', monospace);
  color: var(--med-text-primary, #1a2333);
  border-left: 1px solid var(--med-border, #e2e8f0);
  border-right: 1px solid var(--med-border, #e2e8f0);
}

.mpc-btns { display: flex; flex-direction: column; gap: 8px; }
.mpc-btn-primary, .mpc-btn-outline {
  height: var(--med-btn-height-md, 38px);
  border-radius: var(--med-radius-md, 8px);
  font-family: inherit; font-size: 14px; font-weight: 600;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; cursor: pointer; text-decoration: none;
  transition: all 150ms ease;
}
.mpc-btn-primary {
  background: var(--med-primary, #0a7ca5);
  color: var(--med-text-inverse, #fff);
  border: none;
}
.mpc-btn-primary:hover:not(:disabled) {
  background: var(--med-primary-dark, #086a8e);
  color: var(--med-text-inverse, #fff);
  transform: translateY(-1px);
}
.mpc-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.mpc-btn-outline {
  background: transparent;
  color: var(--med-primary, #0a7ca5);
  border: 1.5px solid var(--med-primary, #0a7ca5);
}
.mpc-btn-outline:hover { background: var(--med-primary-light, #e6f5fa); color: var(--med-primary, #0a7ca5); }

/* ── Small screen: sirf image, title, price aur buttons ── */
@media (max-width: 575px) {
  .mpc-img-wrap { aspect-ratio: 1 / 1.1; }          /* image thodi lambi */
  .mpc-badges { top: 8px; left: 8px; gap: 4px; }
  .mpc-badge { font-size: 10px; padding: 2px 8px; }
  .mpc-actions { top: 8px; right: 8px; gap: 4px; }
  .mpc-icon-btn { width: 30px; height: 30px; }

  .mpc-body { padding: 10px 10px 6px; gap: 4px; flex: 0 0 auto; }
  .mpc-meta,
  .mpc-sku,
  .mpc-desc,
  .mpc-qty-row,
  .mpc-save { display: none; }                      /* mobile pe nahi dikhana */

  .mpc-title {
    font-size: 13px; line-height: 1.3;
    display: -webkit-box; -webkit-line-clamp: 2;
    -webkit-box-orient: vertical; overflow: hidden;
  }

  .mpc-footer { padding: 8px 10px 10px; gap: 8px; border-top: 0; margin-top: auto; }
  .mpc-price { font-size: 16px; }
  .mpc-price-del { font-size: 11px; }
  .mpc-btns { gap: 6px; }
  .mpc-btn-primary, .mpc-btn-outline {
    height: 34px; font-size: 12.5px; gap: 5px;
  }
}
`;

function Card({ item }) {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const { config } = useSiteConfig();

  const isVariant = item?.productType === "Variant";
  const firstVariant = isVariant ? item?.variants?.[0] : null;

  const initialPrice = isVariant ? (firstVariant?.price ?? 0) : (item?.price ?? 0);
  const initialMrp = isVariant ? (firstVariant?.mrp ?? 0) : (item?.mrp ?? 0);
  const initialDiscount = isVariant ? (firstVariant?.discount ?? 0) : (item?.discount ?? 0);
  const initialStock = isVariant ? (firstVariant?.stock ?? 0) : (item?.stock ?? 0);

  const [image] = useState(item?.thumbnail);
  const [slug] = useState(item?.slug);
  const [title] = useState(item?.title);

  const [activePrice, setActivePrice] = useState(initialPrice);
  const [activeMrp, setActiveMrp] = useState(initialMrp);
  const [activeDiscount, setActiveDiscount] = useState(initialDiscount);
  const [activeStock, setActiveStock] = useState(initialStock);
  const [activeVariantId] = useState(
    isVariant && item?.variants?.length ? item?.variants[0]._id : null,
  );
  const [isWishlisted, setIsWishlisted] = useState(item?.isWishlisted || false);

  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (item?.productType !== "Variant") return;
    const selected = item?.variants?.find((v) => v._id === activeVariantId);
    if (selected) {
      setActivePrice(selected.price);
      setActiveMrp(selected.mrp);
      setActiveDiscount(selected.discount);
      setActiveStock(selected.stock);
    }
  }, [activeVariantId, item]);

  const hasDiscount =
    activeDiscount !== null && activeDiscount > 0 && activeMrp > activePrice;
  const isOutOfStock = activeStock <= 0;
  const isLowStock = !isOutOfStock && activeStock <= 10;
  const canBuy = config?.productVisibility === "cart";

  // optional fields (backend de to dikhenge)
  const category = item?.category?.name || item?.categoryName;
  const sku = item?.sku;
  const description = item?.shortDescription || item?.description;
  const rating = Number(item?.rating) || 0;
  const reviewCount = item?.reviewCount ?? item?.reviews;

  const fmt = (n) => Number(n).toLocaleString("en-IN");

  const handleAddToCart = (e) => {
    e.preventDefault();
    if (added || adding) return;

    // UI turant badlo — server ka wait nahi
    setAdding(true);
    setAdded(true);

    addToCart(item?._id, qty, activeVariantId || "").then((ok) => {
      setAdding(false);
      if (ok) {
        setTimeout(() => setAdded(false), 1800);
      } else {
        setAdded(false); // fail hua to button wapas normal
      }
    });
  };

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    const isLoggedIn = Auth.getToken();

    if (!isLoggedIn) {
      showError("Please log in to manage your wishlist");
      return;
    }

    try {
      const res = await wishlistAPI.addProduct({ productId: item?._id });
      if (res.code === 200) {
        setIsWishlisted((prev) => !prev);
      }
    } catch (error) {
      showError("Failed to update wishlist");
    }
  };

  return (
    <>
      <style>{styles}</style>

      <div className="mpc-card">
        {/* ── Image ── */}
        <div className="mpc-img-wrap">
          <Link to={`/shop/${slug}`} className="mpc-img-link">
            <img src={image} alt={title} loading="lazy" />
          </Link>

          <div className="mpc-badges">
            {hasDiscount && (
              <span className="mpc-badge mpc-badge-danger">
                <Zap size={10} />
                {Math.round(activeDiscount)}% OFF
              </span>
            )}
            {isOutOfStock && (
              <span className="mpc-badge mpc-badge-warning">
                <Package size={10} />
                Out of Stock
              </span>
            )}
            {isLowStock && (
              <span className="mpc-badge mpc-badge-warning">
                <Package size={10} />
                Only {activeStock} left
              </span>
            )}
          </div>

          <div className="mpc-actions">
            <button
              className={`mpc-icon-btn ${isWishlisted ? "active" : ""}`}
              onClick={handleWishlistToggle}
              aria-label="Add to wishlist"
              title="Wishlist"
            >
              <Heart size={15} fill={isWishlisted ? "currentColor" : "none"} />
            </button>
            <Link
              to={`/shop/${slug}`}
              className="mpc-icon-btn"
              aria-label="View product"
              title="View product"
            >
              <Eye size={15} />
            </Link>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="mpc-body">
          {(category || rating > 0) && (
            <div className="mpc-meta">
              <span className="mpc-category">{category || ""}</span>
              {rating > 0 && (
                <div className="mpc-rating">
                  <Star size={13} fill="currentColor" strokeWidth={0} />
                  <span>{rating.toFixed(1)}</span>
                  {reviewCount ? (
                    <span className="mpc-rating-count">
                      ({Number(reviewCount).toLocaleString()})
                    </span>
                  ) : null}
                </div>
              )}
            </div>
          )}

          <h3 className="mpc-title">
            <Link to={`/shop/${slug}`}>
              {title}
              {item?.certified && (
                <BadgeCheck
                  size={15}
                  style={{ marginLeft: 6, color: "var(--med-success)", verticalAlign: "middle" }}
                />
              )}
            </Link>
          </h3>

          {/* {sku && <div className="mpc-sku">SKU: {sku}</div>} */}

          {description && <p className="mpc-desc">{description}</p>}
        </div>

        {/* ── Footer ── */}
        <div className="mpc-footer">
          <div className="mpc-price-row">
            <span className="mpc-price">₹{fmt(activePrice)}</span>
            {hasDiscount && (
              <>
                <span className="mpc-price-del">₹{fmt(activeMrp)}</span>
                <span className="mpc-save">{Math.round(activeDiscount)}% off</span>
              </>
            )}
          </div>

          {canBuy && !isOutOfStock && (
            <div className="mpc-qty-row">
              <span className="mpc-qty-label">Qty</span>
              <div className="mpc-qty-ctrl">
                <button
                  className="mpc-qty-btn"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  disabled={qty <= 1}
                  aria-label="Decrease quantity"
                >
                  <Minus size={13} />
                </button>
                <span className="mpc-qty-val">{qty}</span>
                <button
                  className="mpc-qty-btn"
                  onClick={() => setQty((q) => Math.min(activeStock, q + 1))}
                  disabled={qty >= activeStock}
                  aria-label="Increase quantity"
                >
                  <Plus size={13} />
                </button>
              </div>
            </div>
          )}

          <div className="mpc-btns">
            {canBuy && (
              <button
                className="mpc-btn-primary"
                onClick={handleAddToCart}
                disabled={isOutOfStock}
              >
                <ShoppingCart size={15} />
                {isOutOfStock ? "Out of Stock" : added ? "Added to Cart ✓" : "Add to Cart"}
              </button>
            )}
            {/* <Link to={`/shop/${slug}`} className="mpc-btn-outline">
              View Details <ChevronRight size={14} />
            </Link> */}
          </div>
        </div>
      </div>
    </>
  );
}

export default Card;